/**
 * Data processing utilities for HardwarePulse frontend
 * Handles data transformation and cleaning for consistent JSON structure
 */

// Import JSON data directly
import articlesData from '@/data/articles.json';
import sectionsData from '@/data/sections.json';

/**
 * Decodes HTML entities from encoded content
 * @param {string} encodedContent - Content with encoded HTML entities
 * @returns {string} - Decoded HTML content
 */
export function decodeHtmlContent(encodedContent) {
  if (!encodedContent || typeof encodedContent !== 'string') {
    return '';
  }

  return encodedContent
    // Remove code block markers if present
    .replace(/^```html\n/, '')
    .replace(/\n```$/, '')
    // Decode HTML entities
    .replace(/\\u003C/g, '<')
    .replace(/\\u003E/g, '>')
    .replace(/\\u0027/g, "'")
    .replace(/\\u0022/g, '"')
    .replace(/\\u0026/g, '&')
    // Remove any remaining unicode escapes
    .replace(/\\u[0-9a-fA-F]{4}/g, (match) => {
      const code = parseInt(match.substring(2), 16);
      return String.fromCharCode(code);
    });
}

/**
 * Extracts plain text excerpt from HTML content
 * @param {string} htmlContent - HTML content string
 * @param {number} maxLength - Maximum length of excerpt (default: 200)
 * @returns {string} - Plain text excerpt
 */
export function extractTextExcerpt(htmlContent, maxLength = 200) {
  if (!htmlContent) return '';
  
  // Remove HTML tags and get plain text
  const plainText = htmlContent
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  // Truncate at word boundary
  const truncated = plainText.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 0 
    ? truncated.substring(0, lastSpace) + '...'
    : truncated + '...';
}

/**
 * Processes and normalizes article data from different sources
 * @param {Array} rawArticles - Raw article data array
 * @returns {Array} - Normalized article data
 */
export function processArticles(rawArticles) {
  if (!Array.isArray(rawArticles)) {
    return [];
  }

  return rawArticles.map(article => {
    const processed = { ...article };

    // Decode HTML content if it's encoded
    if (processed.content && typeof processed.content === 'string') {
      processed.content = decodeHtmlContent(processed.content);
    }

    // Ensure we have a proper description/excerpt
    if (!processed.description && processed.content) {
      processed.description = extractTextExcerpt(processed.content, 200);
    }

    // Add reviewText property as alias for description (for backwards compatibility)
    processed.reviewText = processed.description;

    // Ensure date is properly formatted
    if (processed.date && !processed.date.includes('T')) {
      processed.date = processed.date + 'T00:00:00Z';
    }

    // Add missing properties with defaults
    processed.readingTimeMinutes = processed.readingTimeMinutes || 
      Math.max(1, Math.ceil(extractTextExcerpt(processed.content, 10000).split(' ').length / 200));
    
    processed.isPublished = processed.isPublished !== undefined ? processed.isPublished : true;
    processed.isFeatured = processed.isFeatured !== undefined ? processed.isFeatured : false;
    processed.isTrending = processed.isTrending !== undefined ? processed.isTrending : false;

    // Initialize metrics if not present
    if (!processed.metrics) {
      processed.metrics = {
        viewCount: Math.floor(Math.random() * 2000) + 100,
        shareCount: Math.floor(Math.random() * 100) + 10,
        commentCount: Math.floor(Math.random() * 50) + 5,
        engagementScore: Math.round((Math.random() * 5 + 5) * 10) / 10,
        lastViewed: new Date().toISOString()
      };
    }

    // Initialize SEO data if not present
    if (!processed.seo) {
      processed.seo = {
        metaTitle: processed.title,
        metaDescription: processed.description,
        keywords: processed.tags || [],
        canonicalUrl: `https://hardwarepulse.com/article/${processed.slug}`
      };
    }

    // Initialize affiliate links if not present
    if (!processed.affiliateLinks) {
      processed.affiliateLinks = [];
    }

    return processed;
  });
}

/**
 * Filters articles by section
 * @param {Array} articles - Article array
 * @param {string|Array} sections - Section name(s) to filter by
 * @returns {Array} - Filtered articles
 */
export function filterBySection(articles, sections) {
  if (!Array.isArray(articles)) return [];
  
  const sectionArray = Array.isArray(sections) ? sections : [sections];
  return articles.filter(article => 
    sectionArray.includes(article.section)
  );
}

/**
 * Sorts articles by date (newest first)
 * @param {Array} articles - Article array
 * @returns {Array} - Sorted articles
 */
export function sortByDate(articles) {
  if (!Array.isArray(articles)) return [];
  
  return [...articles].sort((a, b) => {
    const dateA = new Date(a.date || a.lastModified || 0);
    const dateB = new Date(b.date || b.lastModified || 0);
    return dateB - dateA;
  });
}

/**
 * Gets featured articles
 * @param {Array} articles - Article array
 * @param {number} limit - Maximum number of articles to return
 * @returns {Array} - Featured articles
 */
export function getFeaturedArticles(articles, limit = 5) {
  if (!Array.isArray(articles)) return [];
  
  return articles
    .filter(article => article.isFeatured)
    .slice(0, limit);
}

/**
 * Gets trending articles
 * @param {Array} articles - Article array  
 * @param {number} limit - Maximum number of articles to return
 * @returns {Array} - Trending articles
 */
export function getTrendingArticles(articles, limit = 5) {
  if (!Array.isArray(articles)) return [];
  
  return articles
    .filter(article => article.isTrending)
    .slice(0, limit);
}

/**
 * Loads and processes articles from JSON data
 * @returns {Array} - Processed articles array
 */
export function loadArticles() {
  return processArticles(articlesData);
}

/**
 * Loads sections data
 * @returns {Array} - Sections data array
 */
export function loadSections() {
  return sectionsData;
}

/**
 * Gets articles sorted by date (newest first)
 * @param {number} limit - Maximum number of articles to return
 * @returns {Array} - Sorted articles
 */
export function getLatestArticles(limit = null) {
  const articles = loadArticles();
  const sorted = sortByDate(articles);
  return limit ? sorted.slice(0, limit) : sorted;
}

/**
 * Gets all processed articles data
 * @returns {Array} - All articles data
 */
export function getAllArticles() {
  return loadArticles();
}
